/**
 * The reason a session was ended, carried across the sign-out reload.
 *
 * `AuthService.logOut()` terminates Firestore and finishes with
 * `window.location.reload()`, so a toast raised just before it is destroyed a moment
 * later — the user sees a flash and lands on the login form with nothing to explain
 * why. The reason is parked here instead and raised again once the reload settles.
 *
 * sessionStorage rather than a service field for the same reason: nothing in memory
 * survives the reload.
 */
const STORAGE_KEY = 'ag-crm:sign-out-notice';

export interface SignOutNotice {
  title: string;
  message: string;
}

interface ParkedSignOutNotice extends SignOutNotice {
  /** Epoch ms, compared against this page's load time. See `takeSignOutNotice`. */
  parkedAt: number;
}

export const parkSignOutNotice = (notice: SignOutNotice): void => {
  try {
    const parked: ParkedSignOutNotice = { ...notice, parkedAt: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(parked));
  } catch {
    // Storage can be unavailable (private browsing, blocked cookies). Losing the
    // notice is worse than the sign-out failing, so this never throws.
  }
};

/**
 * Reads the notice and clears it, so a later reload does not show it again.
 *
 * Only a notice parked by a PREVIOUS page is returned. `logOut()` navigates to the
 * login form and only then reloads, so this component mounts twice: once in the page
 * that is about to be torn down, and again in the fresh one. Consuming it on that
 * first mount cleared the notice and left the reload to destroy the toast — which is
 * the whole failure this notice exists to avoid.
 *
 * `performance.timeOrigin` is when the CURRENT page began, so a notice parked before
 * it belongs to the page that has since gone away.
 */
export const takeSignOutNotice = (): SignOutNotice | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parked = JSON.parse(raw) as ParkedSignOutNotice;

    if (typeof parked.parkedAt === 'number' && parked.parkedAt >= performance.timeOrigin) {
      return null;
    }

    sessionStorage.removeItem(STORAGE_KEY);

    return { title: parked.title, message: parked.message };
  } catch {
    return null;
  }
};
